import { useHasAccess } from 'lib-shared-access-control';
import { useEffect, useMemo, useState } from 'react';
import { useMount } from 'react-use';

import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

type UseDashboardViewInitResult = {
  isInitialized: boolean;
};

/**
 * A hook that handles initializing the dashboard view based on URL params and
 * other conditions such as persisted user preferences.
 */
export const useDashboardViewInit = (): UseDashboardViewInitResult => {
  const { params } = useAppSearchParams('/dashboard/queues');
  const navigate = useAppNavigate();
  const hasAnalyticsAccess = useHasAccess('analytics');

  const [isInitialized, setIsInitialized] = useState(false);

  // Handles the case where the queue type in the URL search params is not found.
  // This can happen when accessing the page via a link to the /dashboard/queues path.
  // In this case, we redirect to the default view based on user permissions.
  useMount(() => {
    if (params.type) return;

    navigate(
      {
        path: '/dashboard/queues',
        params: {
          search: {
            type: hasAnalyticsAccess ? 'all' : 'agent',
          },
        },
      },
      { replace: true },
    );
  });

  // Updates the init status when a supported `type` URL search param is found.
  useEffect(() => {
    if (params.type && !isInitialized) {
      setIsInitialized(true);
    }

    // Only run this effect when the URL param changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.type]);

  return useMemo(() => ({ isInitialized }), [isInitialized]);
};
